import { useEffect, useState } from 'react';

/* ─── Animated counter (counts up with easing) ────────────────────────── */
const useCountUp = (target, durationMs = 2600) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return val;
};

/* ─── Constellation network: SVG nodes + animated lines + traveling pulses ── */
const NODES = [
  { x: 70,  y: 110, r: 4 },
  { x: 200, y: 60,  r: 5 },
  { x: 330, y: 130, r: 4 },
  { x: 130, y: 220, r: 5 },
  { x: 270, y: 220, r: 4 },
  { x: 70,  y: 320, r: 4 },
  { x: 210, y: 350, r: 5 },
  { x: 340, y: 290, r: 4 },
];
const EDGES = [
  [0, 1], [1, 2], [0, 3], [1, 3], [2, 4],
  [3, 4], [3, 5], [4, 6], [5, 6], [6, 7], [4, 7], [2, 7],
];

const Constellation = () => (
  <svg
    viewBox="0 0 400 400"
    className="absolute inset-0 w-full h-full"
    preserveAspectRatio="xMidYMid slice"
    aria-hidden="true"
  >
    <defs>
      <radialGradient id="node-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%"  stopColor="#bfdbfe" stopOpacity="1" />
        <stop offset="60%" stopColor="#60a5fa" stopOpacity="0.4" />
        <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="line-grad" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%"   stopColor="#93c5fd" stopOpacity="0" />
        <stop offset="50%"  stopColor="#bfdbfe" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#93c5fd" stopOpacity="0" />
      </linearGradient>
    </defs>

    {/* Edges */}
    {EDGES.map(([a, b], i) => {
      const A = NODES[a], B = NODES[b];
      return (
        <line
          key={i}
          x1={A.x} y1={A.y} x2={B.x} y2={B.y}
          stroke="#bfdbfe"
          strokeWidth="0.8"
          className="animate-pulse-line"
          style={{ animationDelay: `${(i * 0.35) % 3.5}s` }}
        />
      );
    })}

    {/* Traveling pulses along select edges */}
    {EDGES.filter((_, i) => i % 2 === 0).map(([a, b], i) => {
      const A = NODES[a], B = NODES[b];
      return (
        <circle key={`p-${i}`} r="2.5" fill="#dbeafe">
          <animate
            attributeName="cx"
            from={A.x} to={B.x}
            dur={`${3 + (i % 3)}s`}
            begin={`${i * 0.6}s`}
            repeatCount="indefinite"
          />
          <animate
            attributeName="cy"
            from={A.y} to={B.y}
            dur={`${3 + (i % 3)}s`}
            begin={`${i * 0.6}s`}
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0;1;1;0"
            dur={`${3 + (i % 3)}s`}
            begin={`${i * 0.6}s`}
            repeatCount="indefinite"
          />
        </circle>
      );
    })}

    {/* Nodes (glow + core) */}
    {NODES.map((n, i) => (
      <g key={i}>
        <circle cx={n.x} cy={n.y} r={n.r * 4} fill="url(#node-glow)" opacity="0.6" />
        <circle cx={n.x} cy={n.y} r={n.r} fill="#ffffff" />
      </g>
    ))}
  </svg>
);

/* ─── Perspective grid floor (Tron-style) ────────────────────────────── */
const PerspectiveGrid = () => (
  <div className="absolute inset-x-0 bottom-0 h-[55%] [perspective:700px] overflow-hidden opacity-50 pointer-events-none">
    <div
      className="absolute inset-x-[-20%] -bottom-32 h-[200%] animate-grid-pan origin-bottom"
      style={{
        backgroundImage:
          'linear-gradient(rgba(147,197,253,0.25) 1px, transparent 1px), linear-gradient(90deg, rgba(147,197,253,0.25) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
        transform: 'perspective(700px) rotateX(62deg)',
        maskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)',
      }}
    />
  </div>
);

/* ─── Aurora mesh: layered blurred gradient blobs ────────────────────── */
const Aurora = () => (
  <>
    <div className="absolute top-[10%] left-[5%] w-[420px] h-[420px] rounded-full bg-sky-400/40 blur-[100px] animate-aurora-1 mix-blend-screen pointer-events-none" />
    <div className="absolute top-[40%] right-[-10%] w-[460px] h-[460px] rounded-full bg-indigo-500/40 blur-[120px] animate-aurora-2 mix-blend-screen pointer-events-none" />
    <div className="absolute bottom-[-5%] left-[20%] w-[520px] h-[520px] rounded-full bg-cyan-300/30 blur-[130px] animate-aurora-3 mix-blend-screen pointer-events-none" />
    <div className="absolute top-[20%] right-[20%] w-[260px] h-[260px] rounded-full bg-fuchsia-500/15 blur-[90px] animate-aurora-2 mix-blend-screen pointer-events-none" />
  </>
);

/* ─── Drifting particles ─────────────────────────────────────────────── */
const Particles = () => {
  const particles = Array.from({ length: 18 }, (_, i) => ({
    left: `${(i * 53) % 100}%`,
    size: 1 + (i % 3),
    delay: `${(i * 0.7) % 14}s`,
    duration: `${10 + (i % 6)}s`,
    opacity: 0.4 + ((i % 4) * 0.15),
  }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p, i) => (
        <span
          key={i}
          className="absolute bottom-0 rounded-full bg-white animate-drift-up"
          style={{
            left: p.left,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDelay: p.delay,
            animationDuration: p.duration,
            opacity: p.opacity,
          }}
        />
      ))}
    </div>
  );
};

/* ─── Pulsing radial rings emanating from a focal point ───────────────── */
const PingRings = () => (
  <div className="absolute top-[30%] right-[18%] w-3 h-3 pointer-events-none">
    <span className="absolute inset-0 rounded-full bg-sky-200" />
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="absolute inset-0 rounded-full border border-sky-200/60 animate-ping-ring"
        style={{ animationDelay: `${i * 1.05}s` }}
      />
    ))}
  </div>
);

/* ─── 3D-tilted floating UI cards ────────────────────────────────────── */
const TiltedCards = () => {
  const candidates = useCountUp(248);
  const score      = useCountUp(94);
  return (
    <div
      className="absolute inset-0 flex items-center justify-center pointer-events-none"
      style={{ perspective: '1200px' }}
    >
      <div className="relative w-[420px] h-[420px] animate-tilt-soft" style={{ transformStyle: 'preserve-3d' }}>
        {/* Card 1 — Candidates with sparkline */}
        <div
          className="absolute top-2 -left-6 w-56 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-4 ring-1 ring-white/40"
          style={{ transform: 'translateZ(60px)' }}
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Candidates</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
              </svg>
              12%
            </span>
          </div>
          <p className="text-3xl font-bold text-slate-900 mt-1 tabular-nums tracking-tight">{candidates}</p>
          <svg viewBox="0 0 200 50" className="mt-2 w-full h-8">
            <defs>
              <linearGradient id="spark" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#3b82f6" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d="M0,40 L25,30 L50,34 L75,18 L100,22 L125,10 L150,16 L175,6 L200,12 L200,50 L0,50 Z" fill="url(#spark)" />
            <path d="M0,40 L25,30 L50,34 L75,18 L100,22 L125,10 L150,16 L175,6 L200,12" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* Card 2 — Top performer */}
        <div
          className="absolute top-32 right-0 w-52 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-4 ring-1 ring-white/40 animate-float-slow"
          style={{ transform: 'translateZ(80px)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-gradient flex items-center justify-center text-white font-bold ring-2 ring-white/50">RS</div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">Top performer</p>
              <div className="flex items-center gap-1 text-amber-500 text-xs">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.95a1 1 0 00.95.69h4.155c.969 0 1.371 1.24.588 1.81l-3.362 2.443a1 1 0 00-.364 1.118l1.287 3.95c.3.922-.755 1.688-1.54 1.118L10 14.347l-3.362 2.44c-.784.57-1.838-.196-1.539-1.118l1.287-3.95a1 1 0 00-.364-1.118L2.66 8.16c-.783-.57-.38-1.81.588-1.81h4.155a1 1 0 00.95-.69l1.286-3.95z" />
                </svg>
                <span className="font-bold">9.4 / 10</span>
              </div>
            </div>
          </div>
          <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-primary-gradient rounded-full" style={{ width: '94%' }} />
          </div>
        </div>

        {/* Card 3 — Live assessment score gauge */}
        <div
          className="absolute bottom-0 left-8 w-60 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-4 ring-1 ring-white/40 animate-float-slow-delay"
          style={{ transform: 'translateZ(40px)' }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Assessment</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-primary-600">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
              LIVE
            </span>
          </div>
          <div className="flex items-end gap-3">
            <p className="text-4xl font-bold text-slate-900 leading-none tabular-nums">
              {score}<span className="text-lg text-slate-400 font-semibold">%</span>
            </p>
            <p className="text-[11px] text-slate-500 pb-1.5">accuracy</p>
          </div>
          <div className="mt-3 grid grid-cols-10 gap-0.5">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className={`h-1.5 rounded-full ${i < Math.round(score / 10) ? 'bg-primary-500' : 'bg-slate-200'}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── The full cinematic backdrop ────────────────────────────────────── */
const AuthBackdrop = () => (
  <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
    {/* Layer 1: Aurora mesh */}
    <Aurora />

    {/* Layer 2: Perspective grid floor */}
    <PerspectiveGrid />

    {/* Layer 3: Constellation network */}
    <div className="absolute inset-0 opacity-80">
      <Constellation />
    </div>

    {/* Layer 4: Pulsing focal point */}
    <PingRings />

    {/* Layer 5: Drifting particles */}
    <Particles />

    {/* Layer 6: 3D-tilted floating cards */}
    <TiltedCards />

    {/* Layer 7: Vignette overlay */}
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-primary-950/40 pointer-events-none" />
  </div>
);

export default AuthBackdrop;
